import { Check } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@platform/auth-client';
import { Badge, PageHeader } from '@platform/design-system';
import { isLocale, SUPPORTED_LOCALES, useTranslation, type Locale } from '@platform/i18n';
import type { TypedClient } from '@platform/openapi';
import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormProvider, useForm, type FieldPath } from 'react-hook-form';
import { nameIn, type Category } from '../categories.ts';
import { indexOptions, type CatalogOption } from '../options.ts';
import {
  formFromProduct,
  productFormIssues,
  productFormSchema,
  type ProductFormIssue,
  type ProductFormRules,
  type ProductFormValues,
} from '../product-form.ts';
import { COST_ROLE, productStatusTone, type Product, type TenantDetail } from '../product.ts';
import { EditorActions } from './EditorActions.tsx';
import { SECTION_ICONS } from './EditorSectionCard.tsx';
import { EssentialsSection } from './EssentialsSection.tsx';
import { ErrorSummary } from './ErrorSummary.tsx';
import { MediaSection } from './MediaSection.tsx';
import { PricingSection } from './PricingSection.tsx';
import styles from './ProductEditor.module.css';
import { EDITOR_SECTIONS, sectionElementId, sectionOfIssue } from './sections.ts';
import { ShippingSection } from './ShippingSection.tsx';
import { SingleFieldSection } from './SingleFieldSection.tsx';
import { hasStockHistory, StockSection } from './StockSection.tsx';
import { NewDepotSheet } from '../../inventory/NewDepotSheet.tsx';
import { ProductStatisticsPanel } from '../stats/ProductStatisticsPanel.tsx';
import { useDraftAutosave } from './useDraftAutosave.ts';
import { useProductActions, type SaveOutcome } from './useProductActions.ts';
import { VariantsSection } from './VariantsSection.tsx';
import { AdjustStockSheet } from '../../inventory/AdjustStockSheet.tsx';
import { INVENTORY_WRITE_ROLE, type Location } from '../../inventory/inventory.ts';
import { productNameOf, variantRows } from '../product-stock.ts';
import { useStockMatrix } from '../../inventory/useStockMatrix.ts';
import { usePageContext } from '../../usePageContext.ts';
import { BackCrumb } from '../../BackCrumb.tsx';

export type ProductEditorProps = {
  api: TypedClient;
  product: Product | null;
  tenant: TenantDetail;
  categories: readonly Category[];
  options: readonly CatalogOption[];
  onSaved: (saved: Product, outcome: SaveOutcome) => void;
  onArchived: () => void;
  onBack: () => void;
  onDirtyChange: (dirty: boolean) => void;
};

const savedStatusToKeep = (status: Product['status']): Product['status'] =>
  status === 'archived' ? 'draft' : status;

const defaultLocaleOf = (product: Product | null, tenant: TenantDetail): Locale => {
  const preferred = [product?.defaultLocale, ...tenant.locales].find(
    (locale): locale is Locale => locale !== undefined && isLocale(locale),
  );
  return preferred ?? SUPPORTED_LOCALES[0];
};

export const ProductEditor = ({
  api,
  product,
  tenant,
  categories,
  options,
  onSaved,
  onArchived,
  onBack,
  onDirtyChange,
}: ProductEditorProps) => {
  const { t, i18n } = useTranslation();
  const { describeError, locale: viewerLocale } = usePageContext();
  const auth = useAuth();
  const canSeeCost = auth.hasRole(COST_ROLE);
  const canWriteStock = auth.hasRole(INVENTORY_WRITE_ROLE);
  const rules: ProductFormRules = { initialStockRequired: product === null && canWriteStock };
  const defaultLocale = defaultLocaleOf(product, tenant);
  const index = useMemo(() => indexOptions(options, i18n.language), [options, i18n.language]);
  const stock = useStockMatrix(api, product?.variants.map((variant) => variant.id) ?? []);
  const [nameLocale, setNameLocale] = useState<Locale>(defaultLocale);
  const [adjusting, setAdjusting] = useState(false);
  const [depotCreated, setDepotCreated] = useState<((depot: Location) => void) | null>(null);
  const adjustableProduct =
    product !== null &&
    canWriteStock &&
    product.variants.some((variant) => hasStockHistory(stock.levels, variant.id))
      ? product
      : null;
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema(rules)),
    defaultValues: formFromProduct(product, defaultLocale, index),
    mode: 'onBlur',
    shouldFocusError: false,
  });
  const { isDirty, submitCount } = form.formState;
  const actions = useProductActions({
    api,
    product,
    canSeeCost,
    stock: {
      canWrite: canWriteStock,
      acceptsInitialStock: (variantId) => !hasStockHistory(stock.levels, variantId),
    },
    onSaved: (saved, outcome) => {
      form.reset(formFromProduct(saved, defaultLocale, index));
      onSaved(saved, outcome);
    },
    onArchived: () => {
      form.reset();
      onArchived();
    },
  });

  const autosave = useDraftAutosave({
    form,
    enabled: product?.status === 'draft',
    busy: actions.pending,
    persist: actions.autosave,
  });

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const issues = submitCount > 0 ? productFormIssues(form.getValues(), rules) : [];
  const failedSections = new Set(issues.map(sectionOfIssue));

  const focusIssue = (issue: ProductFormIssue) => {
    const [field, locale] = issue.path;
    if (field === 'translations' && typeof locale === 'string' && isLocale(locale))
      flushSync(() => {
        setNameLocale(locale);
      });
    document
      .getElementById(sectionElementId(sectionOfIssue(issue)))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (issue.path.length > 1 || field !== 'variants')
      form.setFocus(issue.path.join('.') as FieldPath<ProductFormValues>);
  };

  const submitAs = (status: Product['status'], outcome: SaveOutcome) => () =>
    void form.handleSubmit(
      (values) => {
        actions.save(values, status, outcome);
      },
      () => {
        const [first] = productFormIssues(form.getValues(), rules);
        if (first !== undefined) focusIssue(first);
      },
    )();

  const title = product === null ? t('productEditor.newTitle') : nameIn(product, i18n.language, product.id);
  const creatorName = product === null ? (auth.user?.name ?? null) : product.createdBy;

  return (
    <div className={styles['page']}>
      <PageHeader
        title={title}
        context={
          creatorName === null || creatorName === ''
            ? t('productEditor.context')
            : t('productEditor.createdBy', { name: creatorName })
        }
        breadcrumbs={[{ label: t('nav.products'), href: '/products' }, { label: title }]}
        renderLink={(crumb) => <BackCrumb label={crumb.label} onBack={onBack} />}
        action={
          product === null ? null : (
            <span className={styles['headerStatus']}>
              <span role="status" aria-live="polite" className={styles[`autosave_${autosave}`]}>
                {autosave === 'saved' ? <Check size={16} aria-hidden="true" /> : null}
                {autosave === 'idle' ? null : t(`productEditor.autosave_${autosave}`)}
              </span>
              <Badge tone={productStatusTone[product.status]}>{t(`catalog.status_${product.status}`)}</Badge>
            </span>
          )
        }
      />
      {actions.error === null ? null : (
        <p role="alert" className={styles['serverError']}>
          {describeError(actions.error) ?? t('errors.UNKNOWN')}
        </p>
      )}
      <ErrorSummary issues={issues} onFocus={focusIssue} />
      <div className={styles['layout']}>
        <nav aria-label={t('productEditor.sections')} className={styles['rail']}>
          <span className={styles['railTitle']} aria-hidden="true">
            {t('productEditor.sections')}
          </span>
          {EDITOR_SECTIONS.map((section) => {
            const Icon = SECTION_ICONS[section];
            return (
              <button
                key={section}
                type="button"
                className={styles['railItem']}
                aria-controls={sectionElementId(section)}
                onClick={() => {
                  document.getElementById(sectionElementId(section))?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span
                  className={[
                    styles['railIcon'],
                    failedSections.has(section) ? styles['railIconError'] : '',
                  ].join(' ')}
                  aria-hidden="true"
                >
                  <Icon size={16} />
                </span>
                {t(`productEditor.${section}`)}
              </button>
            );
          })}
        </nav>
        <FormProvider {...form}>
          <form
            className={styles['sections']}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <EssentialsSection
              api={api}
              categories={categories}
              subsidiaries={tenant.subsidiaries}
              options={options}
              index={index}
              nameLocale={nameLocale}
              onNameLocaleChange={setNameLocale}
            />
            <MediaSection api={api} />
            <PricingSection currency={tenant.currency} canSeeCost={canSeeCost} />
            <VariantsSection
              api={api}
              currency={tenant.currency}
              options={options}
              index={index}
              canSeeCost={canSeeCost}
            />
            <StockSection
              stock={stock}
              onCreateDepot={
                canWriteStock
                  ? (onCreated) => {
                      setDepotCreated(() => onCreated);
                    }
                  : null
              }
              index={index}
              canWriteStock={canWriteStock}
              onAdjustStock={
                adjustableProduct === null
                  ? null
                  : () => {
                      setAdjusting(true);
                    }
              }
            />
            <ShippingSection />
            <SingleFieldSection
              section="more"
              field="warrantyMonths"
              label={t('productEditor.warranty')}
              inputMode="numeric"
            />
          </form>
        </FormProvider>
      </div>
      {product === null ? null : (
        <section aria-labelledby="product-statistics" className={styles['statistics']}>
          <h2 id="product-statistics" className={styles['statisticsTitle']}>
            {t('productStatistics.sectionTitle')}
          </h2>
          <ProductStatisticsPanel api={api} productId={product.id} />
        </section>
      )}
      <EditorActions
        status={product?.status ?? null}
        pending={actions.pending}
        onSave={submitAs(
          product === null ? 'active' : savedStatusToKeep(product.status),
          product === null ? 'created' : 'saved',
        )}
        onActivate={submitAs('active', 'published')}
        onDeactivate={submitAs('draft', 'deactivated')}
        onArchive={actions.archive}
      />
      {adjusting && adjustableProduct !== null ? (
        <AdjustStockSheet
          api={api}
          subtitle={productNameOf(adjustableProduct, viewerLocale)}
          rows={variantRows(adjustableProduct, index)}
          onClose={() => {
            setAdjusting(false);
          }}
        />
      ) : null}
      {depotCreated === null ? null : (
        <NewDepotSheet
          api={api}
          onCreated={(depot) => {
            depotCreated(depot);
            setDepotCreated(null);
          }}
          onClose={() => {
            setDepotCreated(null);
          }}
        />
      )}
    </div>
  );
};
